/**
 * src/lib/api/resumableUpload.js — TICKET-MINIO-RESUMABLE Slice 2 (CEO 2026-06-01).
 *
 * Client side of resumable large-voice upload via S3 multipart to MinIO. Splits
 * the file into ~16 MB parts and PUTs each to a per-part presigned URL; if the
 * upload is interrupted (iOS backgrounds Safari, network drop, app close), it
 * resumes from the last completed part on reopen instead of restarting the file.
 *
 * Talks to the four DORMANT server actions shipped in Slice 1 (`392b2749`,
 * lib/handlers/voice-multipart.js): voice.upload_multipart_{init,sign,complete,
 * abort}. The object key is SERVER-minted at init; the client only ever holds
 * the returned uploadId + objectKey. No new infra — MinIO already does multipart.
 *
 * BOX DEPENDENCY (enablement step, like the nginx buffering fix): the browser
 * can only read each part's ETag response header if MinIO/nginx `/voice-uploads/`
 * sets `Access-Control-Expose-Headers: ETag` on the CORS response. Without it,
 * putPart() fails LOUD (no silent corrupt complete). This module is therefore
 * gated behind VITE_MINIO_RESUMABLE (default OFF) in voice-v2.js so the PROVEN
 * single-PUT path (uploadViaPresigned, validated on a real 188 MB file) stays the
 * default until the box CORS is verified.
 *
 * Seams (apiFetchImpl, xhrImpl) are injectable for tests; prod uses apiFetch +
 * window.XMLHttpRequest. XHR (not fetch) is required for upload.onprogress.
 *
 * Plan: docs/plans/2026-06-01-resumable-upload-plan.md
 */
import { apiFetch, API_BASE } from './client';
import { reportUploadFailure } from './presignedPut';

const VOICE = `${API_BASE}/ai`;

// 16 MB parts -> a 188 MB file is ~12 parts, well under MinIO's 10000-part cap.
export const DEFAULT_PART_SIZE = 16 * 1024 * 1024;
// S3/MinIO require every part EXCEPT the last to be >= 5 MB. The 16 MB default
// satisfies this; callers should not lower it below 5 MB for real uploads.
export const MIN_PART_SIZE = 5 * 1024 * 1024;
const MAX_PARTS = 10000;
// Per-part PUT timeout (Slice 4b). A stalled mobile connection must not hang a part
// forever (which wedges the whole upload + any queue worker driving it) — bound it so
// the part rejects and becomes retryable on the next resume. Generous: a 16 MB part on
// a slow ~1 Mbps link is ~130s; 5 min covers that with margin, well under nginx's 1800s.
export const DEFAULT_PART_TIMEOUT_MS = 300_000;

/**
 * Plan the byte ranges for each part. Pure + deterministic so a resumed run
 * reproduces the exact same ranges (the part size is locked in the persisted
 * state, never recomputed). Returns [{ part, start, end }] (1-based part number).
 */
export function planParts(fileSize, partSize = DEFAULT_PART_SIZE) {
  const size = Number(fileSize);
  const ps = Number(partSize) || DEFAULT_PART_SIZE;
  if (!(size > 0) || !(ps > 0)) return [];
  const parts = [];
  let start = 0;
  let part = 1;
  while (start < size) {
    const end = Math.min(start + ps, size);
    parts.push({ part, start, end });
    start = end;
    part += 1;
  }
  if (parts.length > MAX_PARTS) {
    throw new Error(`resumable upload: ${parts.length} parts exceeds the ${MAX_PARTS}-part cap — increase part size`);
  }
  return parts;
}

/**
 * Ask Vercel to begin a multipart upload. The server runs the per-scope authz
 * (mirrors voice.upload_url) and mints the object key, returning an uploadId.
 * @returns {Promise<{uploadId:string, objectKey:string, bucket:string, storageBackend:string}>}
 */
export async function initMultipartUpload({ scopeType, scopeId, file, apiFetchImpl }) {
  const fetchImpl = apiFetchImpl || apiFetch;
  const body = {
    action: 'voice.upload_multipart_init',
    scope_type: scopeType,
    scope_id: scopeId,
    file_name: file?.name,
  };
  if (scopeType === 'project') body.project_id = scopeId; // back-compat alias
  const res = await fetchImpl(VOICE, { method: 'POST', body: JSON.stringify(body) });
  if (!res || !res.upload_id || !res.object_key) {
    throw new Error(`multipart init failed (no upload_id/object_key returned): ${res?.error || ''}`.trim());
  }
  return { uploadId: res.upload_id, objectKey: res.object_key, bucket: res.bucket, storageBackend: res.storage_backend || 'hetzner' };
}

/**
 * Get a short-lived presigned PUT URL for one part. The server re-derives the
 * scope from the object key prefix + re-runs the gate, and requires the
 * server-issued uploadId — so a third party can't drive someone else's upload.
 * @returns {Promise<{url:string, partNumber:number}>}
 */
export async function signPart({ objectKey, uploadId, partNumber, scopeId, apiFetchImpl }) {
  const fetchImpl = apiFetchImpl || apiFetch;
  const body = {
    action: 'voice.upload_multipart_sign',
    object_key: objectKey,
    upload_id: uploadId,
    part_number: partNumber,
    scope_id: scopeId,
  };
  const res = await fetchImpl(VOICE, { method: 'POST', body: JSON.stringify(body) });
  if (!res || !res.url) {
    throw new Error(`multipart sign failed for part ${partNumber} (no url returned): ${res?.error || ''}`.trim());
  }
  return { url: res.url, partNumber: res.part_number ?? partNumber };
}

/**
 * PUT one part blob to its presigned URL with byte progress. Resolves with the
 * part's ETag (REQUIRED for complete) read from the response header. Rejects on
 * non-2xx, network error, OR a missing/unreadable ETag header — the last case is
 * the box-CORS gap and must surface loudly, never produce a silent bad complete.
 * @returns {Promise<{etag:string}>}
 */
export function putPart({ url, blob, onProgress, xhrImpl, timeoutMs = DEFAULT_PART_TIMEOUT_MS }) {
  const XHRImpl = xhrImpl || (typeof XMLHttpRequest !== 'undefined' ? XMLHttpRequest : null);
  if (!XHRImpl) return Promise.reject(new Error('XMLHttpRequest unavailable'));
  return new Promise((resolve, reject) => {
    const xhr = new XHRImpl();
    xhr.open('PUT', url, true);
    // Bound a stalled part so it rejects (and becomes retryable on resume) instead of
    // hanging the upload forever (Slice 4b — queue-wedge cleanup).
    if (timeoutMs > 0) {
      try { xhr.timeout = timeoutMs; } catch { /* spine-emit-ok: timeout set is best-effort */ }
      xhr.ontimeout = () => reject(new Error(`multipart part PUT timed out after ${timeoutMs}ms`));
    }
    if (blob && blob.type) {
      try { xhr.setRequestHeader('Content-Type', blob.type); } catch { /* spine-emit-ok: header set is best-effort */ }
    }
    if (xhr.upload) {
      xhr.upload.onprogress = (e) => { if (onProgress) onProgress(e.loaded, e.total || (blob && blob.size) || 0); };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const raw = (xhr.getResponseHeader && xhr.getResponseHeader('ETag')) || '';
        const etag = String(raw).replace(/"/g, '').trim();
        if (!etag) {
          reject(new Error('multipart part PUT succeeded but the ETag response header was unreadable — MinIO/nginx /voice-uploads/ must CORS-expose ETag (Access-Control-Expose-Headers: ETag)'));
          return;
        }
        resolve({ etag });
      } else {
        reject(new Error(`multipart part PUT failed: ${xhr.status} ${String(xhr.responseText || '').slice(0, 200)}`));
      }
    };
    // TICKET-476 — status 0 / no response. Shared diagnosis + spine emit; a
    // CSP-blocked origin looks identical to an offline box from here.
    xhr.onerror = () => reject(reportUploadFailure({ step: 'voice_multipart.put', url }));
    xhr.send(blob);
  });
}

/**
 * Finalize the multipart upload. Parts are sorted by number and quotes stripped
 * (the server also normalizes). Returns the final object key + backend.
 * @returns {Promise<{objectKey:string, etag:(string|null), storageBackend:string}>}
 */
export async function completeMultipartUpload({ objectKey, uploadId, parts, scopeId, apiFetchImpl }) {
  const fetchImpl = apiFetchImpl || apiFetch;
  const sorted = (Array.isArray(parts) ? parts : [])
    .map((p) => ({ part: Number(p.part), etag: String(p.etag || '').replace(/"/g, '') }))
    .sort((a, b) => a.part - b.part);
  const body = {
    action: 'voice.upload_multipart_complete',
    object_key: objectKey,
    upload_id: uploadId,
    parts: sorted,
    scope_id: scopeId,
  };
  const res = await fetchImpl(VOICE, { method: 'POST', body: JSON.stringify(body) });
  if (!res || res.success === false || (!res.object_key && res.error)) {
    throw new Error(`multipart complete failed: ${res?.error || 'unknown'}`);
  }
  return { objectKey: res.object_key || objectKey, etag: res.etag || null, storageBackend: res.storage_backend || 'hetzner' };
}

/**
 * Abort an in-flight multipart upload (frees the MinIO staged parts). Best-effort
 * — returns the raw server response; callers ignore failures.
 */
export async function abortMultipartUpload({ objectKey, uploadId, scopeId, apiFetchImpl }) {
  const fetchImpl = apiFetchImpl || apiFetch;
  return fetchImpl(VOICE, {
    method: 'POST',
    body: JSON.stringify({ action: 'voice.upload_multipart_abort', object_key: objectKey, upload_id: uploadId, scope_id: scopeId }),
  });
}

/**
 * Orchestrate a full (or resumed) resumable upload.
 *
 * Resume contract: pass the persisted `state` from a prior interrupted run. If it
 * matches THIS file (same byte size), only the missing parts are re-signed + PUT;
 * otherwise a fresh multipart upload is started. `onState(state)` fires after init
 * and after every completed part so the caller (Slice 3 / voiceQueue) can persist
 * resume state to IndexedDB. `onProgress(uploadedBytes, totalBytes)` fires
 * continuously for the "real %" UI.
 *
 * @returns {Promise<{object_key:string, storage_backend:string, bucket:string, etag:(string|null)}>}
 */
export async function uploadResumable({
  file, scopeType, scopeId,
  partSize = DEFAULT_PART_SIZE,
  state = null,
  onProgress, onState,
  apiFetchImpl, xhrImpl,
}) {
  if (!file || !(file.size > 0)) {
    throw new Error('uploadResumable: file is empty (0 bytes) — nothing to upload');
  }

  // Reuse prior state ONLY if it is for this exact file (size match). A size
  // mismatch means a different recording reused the slot — start fresh.
  let session = (state && state.uploadId && state.objectKey && Number(state.fileSize) === Number(file.size))
    ? { ...state, completedParts: Array.isArray(state.completedParts) ? [...state.completedParts] : [] }
    : null;

  if (!session) {
    const init = await initMultipartUpload({ scopeType, scopeId, file, apiFetchImpl });
    session = {
      uploadId: init.uploadId,
      objectKey: init.objectKey,
      bucket: init.bucket,
      storageBackend: init.storageBackend,
      partSize,            // LOCK the part size — resume must split identically
      fileSize: file.size,
      completedParts: [],
    };
    if (onState) await onState(session);
  }

  const effectivePartSize = Number(session.partSize) || partSize;
  const plan = planParts(file.size, effectivePartSize);
  const doneMap = new Map(session.completedParts.map((p) => [Number(p.part), String(p.etag)]));

  const totalBytes = file.size;
  let uploadedBytes = plan
    .filter((p) => doneMap.has(p.part))
    .reduce((acc, p) => acc + (p.end - p.start), 0);
  if (onProgress) onProgress(uploadedBytes, totalBytes);

  for (const p of plan) {
    if (doneMap.has(p.part)) continue;
    const { url } = await signPart({ objectKey: session.objectKey, uploadId: session.uploadId, partNumber: p.part, scopeId, apiFetchImpl });
    const blob = file.slice(p.start, p.end);
    const partBytes = p.end - p.start;
    const { etag } = await putPart({
      url, blob, xhrImpl,
      onProgress: (loaded) => { if (onProgress) onProgress(uploadedBytes + Math.min(loaded, partBytes), totalBytes); },
    });
    uploadedBytes += partBytes;
    session.completedParts.push({ part: p.part, etag });
    session.completedParts.sort((a, b) => a.part - b.part);
    doneMap.set(p.part, etag);
    if (onState) await onState(session);
    if (onProgress) onProgress(uploadedBytes, totalBytes);
  }

  const parts = session.completedParts.map((p) => ({ part: p.part, etag: p.etag }));
  const result = await completeMultipartUpload({ objectKey: session.objectKey, uploadId: session.uploadId, parts, scopeId, apiFetchImpl });
  return {
    object_key: result.objectKey || session.objectKey,
    storage_backend: result.storageBackend || session.storageBackend || 'hetzner',
    bucket: session.bucket,
    etag: result.etag || null,
  };
}
